import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import type { PlanId, RestaurantId } from '~/lib/graphql/scalars'

type Favorites = {
  restaurants: FavoriteRestaurant[]
  plans: FavoritePlan[]
  updated: string
}

type FavoriteRestaurant = {
  restaurantId: RestaurantId
  favoritedAt: string
}

type FavoritePlan = {
  planId: PlanId
  favoritedAt: string
}

const favorites$ = atomWithStorage<Favorites | undefined>('favorite', undefined, undefined, {
  getOnInit: typeof window !== 'undefined',
})
favorites$.debugLabel = 'favorites$'

// #region 店舗
/** お気に入りに入っている店舗IDのリストを取得 */
export const favoriteRestaurantIds$ = atom((get) =>
  (get(favorites$)?.restaurants ?? []).map((r) => r.restaurantId)
)
favoriteRestaurantIds$.debugLabel = 'favoriteRestaurantIds$'

/** 与えられた店舗IDをお気に入りに追加 */
export const addRestaurantToFavorites$ = atom<null, [RestaurantId], void>(
  null,
  (_get, set, restaurantId) => {
    set(favorites$, (prev) => {
      const at = new Date().toISOString()
      return {
        ...(prev ?? { plans: [] }),
        restaurants: [...(prev?.restaurants ?? []), { restaurantId, favoritedAt: at }],
        updated: at,
      }
    })
  }
)
addRestaurantToFavorites$.debugLabel = 'addRestaurantToFavorites$'

/** 与えられた店舗IDをお気に入りから削除 */
export const removeRestaurantFromFavorites$ = atom<null, [RestaurantId], void>(
  null,
  (_get, set, restaurantId) => {
    set(favorites$, (prev) => ({
      ...(prev ?? { plans: [] }),
      restaurants: prev?.restaurants.filter((r) => r.restaurantId !== restaurantId) ?? [],
      updated: new Date().toISOString(),
    }))
  }
)
removeRestaurantFromFavorites$.debugLabel = 'removeRestaurantFromFavorites$'
// #endregion

// #region プラン
/** お気に入りに入っているプランIDのリストを取得 */
export const favoritePlanIds$ = atom((get) => (get(favorites$)?.plans ?? []).map((r) => r.planId))
favoritePlanIds$.debugLabel = 'favoritePlanIds$'

/** 与えられたプランIDをお気に入りに追加 */
export const addPlanToFavorites$ = atom<null, [PlanId], void>(null, (_get, set, planId) => {
  set(favorites$, (prev) => {
    const at = new Date().toISOString()
    return {
      ...(prev ?? { restaurants: [] }),
      plans: [...(prev?.plans ?? []), { planId, favoritedAt: at }],
      updated: at,
    }
  })
})
addPlanToFavorites$.debugLabel = 'addPlanToFavorites$'

/** 与えられたプランIDをお気に入りから削除 */
export const removePlanFromFavorites$ = atom<null, [PlanId], void>(null, (_get, set, planId) => {
  set(favorites$, (prev) => ({
    ...(prev ?? { restaurants: [] }),
    plans: prev?.plans.filter((p) => p.planId !== planId) ?? [],
    updated: new Date().toISOString(),
  }))
})
removePlanFromFavorites$.debugLabel = 'removePlanFromFavorites$'
// #endregion
